import React from 'react';
import { 
  Home, 
  Search, 
  Bot, 
  Briefcase, 
  Bookmark, 
  History,
  FileText,
  Bell,
  User,
  Settings,
  LogOut,
  FolderOpen,
  UploadCloud,
  Layers,
  ChevronRight,
  ChevronLeft,
  Scale,
  Users,
  Database,
  Activity,
  ShoppingBag,
  BriefcaseBusiness,
  ShieldCheck,
  CreditCard,
  MessageSquare,
  FileSignature,
  Mail,
  Info,
  BookOpen,
  Newspaper,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/src/lib/utils';
import { ViewState, User as UserType } from '@/src/types';

interface SidebarProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  user: UserType | null;
  onOpenNewsletter: () => void;
}

// 1. Client / Public User Navigation Menu
const clientNavItems = [
  { icon: Home, label: 'Home Page', view: 'landing' as ViewState },
  { icon: Home, label: 'My Portal', view: 'dashboard' as ViewState },
  { icon: Briefcase, label: 'My Cases', view: 'my-cases' as ViewState },
  { icon: Search, label: 'Search Precedents', view: 'search' as ViewState },
  { icon: BookOpen, label: 'Laws & Acts', view: 'laws' as ViewState },
  { icon: Newspaper, label: 'Legal Blog', view: 'blogs' as ViewState },
  { icon: FileSignature, label: 'Drafting Workspace', view: 'workspace' as ViewState },
  { icon: ShoppingBag, label: 'Legal Experts', view: 'advocate-marketplace' as ViewState },
  { icon: MessageSquare, label: 'Secure Portal', view: 'client-portal' as ViewState },
  { icon: Bot, label: 'AI Legal Assistant', view: 'ai-assistant' as ViewState },
  { icon: FolderOpen, label: 'Saved Library', view: 'repository' as ViewState },
  { icon: Info, label: 'About & Support', view: 'about-me' as ViewState },
  { icon: ShieldCheck, label: 'Privacy Policy', view: 'privacy-policy' as ViewState },
  { icon: FileText, label: 'Terms of Service', view: 'terms-of-service' as ViewState },
  { icon: Scale, label: 'Refund & Cancellation Policy', view: 'refund-cancellation-policy' as ViewState },
  { icon: AlertTriangle, label: 'Legal Disclaimer', view: 'legal-disclaimer' as ViewState },
];

// 2. Lawyer / Advocate Navigation Menu
const advocateNavItems = [
  { icon: Home, label: 'Home Page', view: 'landing' as ViewState },
  { icon: Home, label: 'My Office', view: 'advocate-dashboard' as ViewState },
  { icon: Briefcase, label: 'My Cases', view: 'my-cases' as ViewState },
  { icon: Search, label: 'Search Precedents', view: 'search' as ViewState },
  { icon: BookOpen, label: 'Laws & Acts', view: 'laws' as ViewState },
  { icon: Newspaper, label: 'Legal Blog', view: 'blogs' as ViewState },
  { icon: ShoppingBag, label: 'Legal Experts', view: 'advocate-marketplace' as ViewState },
  { icon: FileSignature, label: 'Drafting Workspace', view: 'workspace' as ViewState },
  { icon: Bot, label: 'AI Legal Assistant', view: 'ai-assistant' as ViewState },
  { icon: FolderOpen, label: 'My Documents', view: 'repository' as ViewState },
  { icon: Info, label: 'About & Support', view: 'about-me' as ViewState },
  { icon: ShieldCheck, label: 'Privacy Policy', view: 'privacy-policy' as ViewState },
  { icon: FileText, label: 'Terms of Service', view: 'terms-of-service' as ViewState },
  { icon: Scale, label: 'Refund & Cancellation Policy', view: 'refund-cancellation-policy' as ViewState },
  { icon: AlertTriangle, label: 'Legal Disclaimer', view: 'legal-disclaimer' as ViewState },
];

// 3. Super Admin Navigation Menu
const adminNavItems = [
  { icon: Home, label: 'Home Page', view: 'landing' as ViewState },
  { icon: Activity, label: 'Admin Dashboard', view: 'admin-dashboard' as ViewState },
  { icon: Search, label: 'Search Precedents', view: 'search' as ViewState },
  { icon: BookOpen, label: 'Laws & Acts', view: 'laws' as ViewState },
  { icon: Newspaper, label: 'Legal Blog', view: 'blogs' as ViewState },
  { icon: ShoppingBag, label: 'Legal Experts', view: 'advocate-marketplace' as ViewState },
  { icon: FileSignature, label: 'Drafting Workspace', view: 'workspace' as ViewState },
  { icon: Bot, label: 'AI Legal Assistant', view: 'ai-assistant' as ViewState },
  { icon: Info, label: 'About & Support', view: 'about-me' as ViewState },
  { icon: ShieldCheck, label: 'Privacy Policy', view: 'privacy-policy' as ViewState },
  { icon: FileText, label: 'Terms of Service', view: 'terms-of-service' as ViewState },
  { icon: Scale, label: 'Refund & Cancellation Policy', view: 'refund-cancellation-policy' as ViewState },
  { icon: AlertTriangle, label: 'Legal Disclaimer', view: 'legal-disclaimer' as ViewState },
];

export function Sidebar({ currentView, onNavigate, isCollapsed, toggleCollapse, user, onOpenNewsletter }: SidebarProps) {
  // Determine menu items based on actual authenticated user role
  const getNavItems = () => {
    if (!user) {
      return [
        { icon: Home, label: 'Home Page', view: 'landing' as ViewState },
        { icon: Search, label: 'Search Precedents', view: 'search' as ViewState },
        { icon: BookOpen, label: 'Laws & Acts', view: 'laws' as ViewState },
        { icon: Newspaper, label: 'Legal Blog', view: 'blogs' as ViewState },
        { icon: ShoppingBag, label: 'Lawyer Marketplace', view: 'advocate-marketplace' as ViewState },
        { icon: Info, label: 'About & Support', view: 'about-me' as ViewState },
        { icon: ShieldCheck, label: 'Privacy Policy', view: 'privacy-policy' as ViewState },
        { icon: FileText, label: 'Terms of Service', view: 'terms-of-service' as ViewState },
        { icon: Scale, label: 'Refund & Cancellation Policy', view: 'refund-cancellation-policy' as ViewState },
        { icon: AlertTriangle, label: 'Legal Disclaimer', view: 'legal-disclaimer' as ViewState },
        { icon: User, label: 'Sign In / Register', view: 'login' as ViewState },
      ];
    }

    if (user.role === 'Super Admin') {
      return adminNavItems;
    }

    if (['Lawyer'].includes(user.role)) {
      return advocateNavItems;
    }

    // Default to client/user
    return clientNavItems;
  };

  const navItems = getNavItems();

  return (
    <div 
      className={cn(
        "flex flex-col bg-slate-900 text-slate-300 transition-all duration-300 h-full border-r border-slate-800",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Platform Branding */}
      <div className="h-16 flex items-center justify-between px-3.5 border-b border-slate-800 bg-slate-950/40">
        {!isCollapsed && (
          <button 
            onClick={() => onNavigate('landing')}
            className="flex items-center gap-3 text-left group overflow-hidden whitespace-nowrap focus:outline-none"
            title="Go to Home"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 border border-emerald-500/25 p-1 flex items-center justify-center shrink-0 shadow-sm group-hover:border-emerald-400/50 transition-colors">
              <img src="/logo.svg" alt="PakLawSite Logo" className="w-full h-full object-contain drop-shadow-md group-hover:scale-105 transition-transform" />
            </div>
            <div className="flex flex-col">
              <span className="font-sans text-base leading-none tracking-tight font-black text-white group-hover:text-emerald-300 transition-colors">
                PakLaw<span className="text-emerald-400 font-extrabold">Site</span>
              </span>
              <span className="text-[9px] font-bold text-slate-500 tracking-wider uppercase mt-1">Legal Intelligence</span>
            </div>
          </button>
        )}
        {isCollapsed && (
          <button 
            onClick={() => onNavigate('landing')}
            className="flex justify-center w-full focus:outline-none group"
            title="PakLawSite - Home"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 border border-emerald-500/25 p-1 flex items-center justify-center shadow-sm group-hover:border-emerald-400/50 transition-colors">
              <img src="/logo.svg" alt="PakLawSite Logo" className="w-full h-full object-contain drop-shadow-md group-hover:scale-105 transition-transform" />
            </div>
          </button>
        )}
      </div>

      {/* User Context Header (Role Display) */}
      {user && !isCollapsed && (
        <div className="p-4 border-b border-slate-800 bg-slate-950/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <User className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-black text-white truncate">{user.name}</p>
              <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider">{user.role}</p>
            </div>
          </div>
        </div>
      )}

      {/* Primary Navigation list */}
      <div className="flex-1 overflow-y-auto py-4 flex flex-col gap-6 scrollbar-hide">
        <div className="flex flex-col gap-1 px-2">
          {!isCollapsed && (
            <p className="px-3 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Navigation Menu
            </p>
          )}
          {navItems.map((item) => {
            const isActive = currentView === item.view || (currentView === 'case-report' && item.view === 'search');
            return (
              <button
                key={item.label}
                id={`nav-${item.view}`}
                onClick={() => onNavigate(item.view)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all font-sans text-sm",
                  isActive 
                    ? "bg-slate-800 text-emerald-400 font-bold shadow-sm" 
                    : "hover:bg-slate-800/40 hover:text-white text-slate-500",
                  isCollapsed && "justify-center px-0 rounded-lg"
                )}
                title={isCollapsed ? item.label : undefined}
                aria-label={isCollapsed ? item.label : undefined}
              >
                <item.icon className="w-5 h-5 shrink-0" />
                {!isCollapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </div>

        {/* Premium Newsletter Subscription Promotion inside Sidebar body if eligible */}
        {(() => {
          const isEligible = !user || user.role === 'Client';
          if (!isEligible) return null;
          return !isCollapsed ? (
            <div className="mx-3 mt-auto p-4 bg-emerald-950/30 rounded-2xl border border-emerald-500/20 text-center transition-all">
              <Mail className="w-6 h-6 text-emerald-400 mx-auto mb-2 shrink-0" />
              <p className="text-xs font-black text-white leading-none">Weekly Legal Intel</p>
              <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                Rulings, critical court updates & drafting suggestions.
              </p>
              <button 
                onClick={onOpenNewsletter}
                id="sidebar-newsletter-subscribe-btn"
                className="mt-3 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs shadow-md transition-all cursor-pointer"
              >
                Subscribe Now
              </button>
            </div>
          ) : (
            <div className="px-2 mt-auto mb-1 text-center">
              <button 
                onClick={onOpenNewsletter}
                id="sidebar-newsletter-subscribe-btn-collapsed"
                className="mx-auto p-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/15 rounded-xl transition-all cursor-pointer flex justify-center items-center"
                title="Subscribe to Precedents Intel"
                aria-label="Subscribe to Precedents Intel"
              >
                <Mail className="w-5 h-5" />
              </button>
            </div>
          );
        })()}
      </div>

      {/* Collapse/Expand Sidebar footer */}
      <div className="p-2 border-t border-slate-800 bg-slate-950/30">
        <button
          onClick={toggleCollapse}
          className="flex items-center justify-center w-full p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
